import {Helmet} from 'react-helmet-async';
import {useTranslation} from 'react-i18next';
import '../../css/frontend/lab-page.css';

export default function Lab() {
    const {t} = useTranslation();

    const TOOL_COUNT = 1000;
    const WORK_AREAS = 13;

    return (
        <>
            <Helmet>
                <title>{t("LAB_TITLE_PAGE") || "SpaceTime Community | Lab"}</title>
                <meta
                    name="description"
                    content={t("LAB_META_DESC") || "Το ανοιχτό εργαστήριο της γειτονιάς: εργαλεία, χώροι και κοινότητα για δημιουργία, επισκευή και μάθηση."}
                />
                <meta property="og:title" content="SpaceTime | Lab"/>
                <meta property="og:url" content="https://spacetime.community/lab"/>
            </Helmet>

            <div className="lab-page">
                {/* Hero */}
                <header className="lab-map">
                    <img
                        src="/img/lab/lab-map.png"
                        alt={t("LAB_HERO_ALT") || "SpaceTime Lab"}
                        className="lab-map-image"
                    />
                    <h1 className="lab-title">{t("LAB_TITLE") || "SpaceTime Lab"}</h1>
                    <p className="lab-subtitle">
                        {t("LAB_SUBTITLE") ||
                            "Μια ανοιχτή εργαλειοθήκη της γειτονιάς, τα μέλη μπορούν να έρθουν, να χρησιμοποιήσουν τα εργαλεία και να υλοποιήσουν τις ιδέες τους."}
                    </p>
                </header>

                {/* Intro */}
                <section className="lab-introcontainer container">
                    <p className="lab-intro-text">
                        {t("LAB_INTRO_TEXT") ||
                            <>
                                Το Lab λειτουργεί ως <strong>ανοιχτό εργαστήριο</strong>: τα μέλη μπορούν να έρθουν στον χώρο και να αξιοποιήσουν τον εξοπλισμό μας. Πρακτικά,
                                είναι η <em>εργαλειοθήκη της κοινότητας</em> για κατασκευές, επισκευές και πειραματισμό.
                            </>}
                    </p>
                </section>

                {/* Stats */}
                <section className="lab-stats-container" aria-label={t("LAB_STATS_LABEL") || "Γρήγορα στατιστικά"}>
                    <div className="stat">
                        <span className="stat-number">{TOOL_COUNT}+</span>
                        <span className="stat-label">{t("LAB_STAT_TOOLS") || "Εργαλεία"}</span>
                    </div>
                    <div className="stat">
                        <span className="stat-number">{WORK_AREAS}</span>
                        <span className="stat-label">{t("LAB_STAT_PROJECTS") || "Ανοιχτά Projects"}</span>
                    </div>
                    <div className="stat lab-stat-open">
                        <span className="stat-label">{t("LAB_STAT_SESSIONS") || "ACTIVE"}</span>
                    </div>
                </section>

                {/* Content grid */}
                <main className="lab-body-container">
                    <div className="lab-grid">
                        {/* Ξυλουργείο */}
                        <article className="lab-card">
                            <img src="/img/lab/woodworking_lathe2.jpg" alt={t("LAB_WOOD_ALT") || "Ξυλουργείο"}
                                 className="lab-card-image"/>
                            <div className="lab-card-body">
                                <h3 className="lab-card-title">{t("LAB_WOOD_TITLE") || "Ξυλουργείο"}</h3>
                                <p className="lab-card-text">
                                    {t("LAB_WOOD_DESC") ||
                                        "Πάγκοι, δράπανα, σέγες, τριβεία, χειροκίνητα εργαλεία. Για ράφια, κουτιά, επιδιορθώσεις επίπλων και custom κατασκευές."}
                                </p>
                            </div>
                        </article>

                        {/* Ηλεκτρονικά & Κολλήσεις (με μικροσκόπιο) */}
                        <article className="lab-card">
                            <img src="/img/lab/electronics_preview.jpg"
                                 alt={t("LAB_ELEC_ALT") || "Ηλεκτρονικά / Κολλήσεις"} className="lab-card-image"/>
                            <div className="lab-card-body">
                                <h3 className="lab-card-title">{t("LAB_ELEC_TITLE") || "Ηλεκτρονικά & Κολλήσεις"}</h3>
                                <p className="lab-card-text">
                                    {t("LAB_ELEC_DESC_WITH_SCOPE") ||
                                        "Κολλητήρια, τροφοδοτικά, πολύμετρα, μικροσκόπιο και μικροεξαρτήματα. Ιδανικό για επισκευές, μικροελεγκτές και πρωτότυπα."}
                                </p>
                            </div>
                        </article>

                        {/* Welding & Spray Area (αντικατάσταση του 3D Printing) */}
                        <article className="lab-card">
                            <img src="/img/lab/welding_preview.jpg" alt={t("LAB_WELD_ALT") || "Welding & Spray Area"}
                                 className="lab-card-image"/>
                            <div className="lab-card-body">
                                <h3 className="lab-card-title">{t("LAB_WELD_TITLE") || "Welding & Spray Area"}</h3>
                                <p className="lab-card-text">
                                    {t("LAB_WELD_DESC") ||
                                        "Συγκολλήσεις και χώρος spray για βαφές/φινιρίσματα μικρής κλίμακας. Εξοπλισμός προστασίας και βασικές οδηγίες ασφαλείας."}
                                </p>
                            </div>
                        </article>

                        {/* Ραπτική */}
                        <article className="lab-card">
                            <img src="/img/lab/fabrication_preview2.jpg" alt={t("LAB_TEXT_ALT") || "Ραπτική & Υφάσματα"}
                                 className="lab-card-image"/>
                            <div className="lab-card-body">
                                <h3 className="lab-card-title">{t("LAB_TEXT_TITLE") || "Ραπτική & Υφάσματα"}</h3>
                                <p className="lab-card-text">
                                    {t("LAB_TEXT_DESC") ||
                                        "Ραπτομηχανή, εργαλεία υφασμάτων και επιδιορθώσεων. Από τσάντες μέχρι banners."}
                                </p>
                            </div>
                        </article>

                        {/* Crafting Area (3D printers & μικροεργαλεία) */}
                        <article className="lab-card">
                            <img src="/img/lab/workbench_area2.jpg" alt={t("LAB_CRAFT_ALT") || "Crafting Area"}
                                 className="lab-card-image"/>
                            <div className="lab-card-body">
                                <h3 className="lab-card-title">{t("LAB_CRAFT_TITLE") || "Crafting Area"}</h3>
                                <p className="lab-card-text">
                                    {t("LAB_CRAFT_DESC") ||
                                        "3D printers, μικροεργαλεία και πάγκοι για πρωτότυπα, επισκευές και λεπτοδουλειές. Ιδανικό για μικρές κατασκευές & μοντελισμό."}
                                </p>
                            </div>
                        </article>

                        {/* Επισκευή Ποδηλάτων */}
                        <article className="lab-card bike">
                            <img src="/img/lab/bicycle_repair_preview.jpg"
                                 alt={t("LAB_BIKE_ALT") || "Επισκευή Ποδηλάτων"} className="lab-card-image"/>
                            <div className="lab-card-body">
                                <h3 className="lab-card-title">{t("LAB_BIKE_TITLE") || "Επισκευή Ποδηλάτων"}</h3>
                                <p className="lab-card-text">
                                    {t("LAB_BIKE_DESC") ||
                                        "Βασικά εργαλεία, στάντ και αναλώσιμα για ρυθμίσεις, αλλαγές και μικροεπισκευές. Workshops με την κοινότητα ποδηλατών."}
                                </p>
                            </div>
                        </article>
                    </div>
                </main>
                {/* Scraps / Capacity */}
                <section className="lab-scraps-container" aria-labelledby="lab-scraps-title">
                    <h2 id="lab-scraps-title" className="lab-section-title">
                        {t("LAB_SCRAPS_TITLE") || "Scrap Capacity"}
                    </h2>
                    <p className="lab-section-subtitle">
                        {t("LAB_SCRAPS_SUBTITLE") || "Πόσο γεμάτοι είμαστε με υλικά προς χρήση/ανακύκλωση."}
                    </p>

                    {/* Ρύθμισε εδώ τα δεδομένα σου */}
                    {[
                        {key: "electronics", label: t("LAB_SCRAPS_ELECTRONICS") || "Electronics", value: 120},
                        {key: "wood", label: t("LAB_SCRAPS_WOOD") || "Ξύλο", value: 80},
                        {key: "paper", label: t("LAB_SCRAPS_PAPER") || "Χαρτί", value: 30},
                        {key: "plastic", label: t("LAB_SCRAPS_PLASTIC") || "Πλαστικό", value: 60},
                    ].map(({key, label, value}) => {
                        const capped = Math.min(value, 100);
                        const isOver = value > 100;
                        const status =
                            value >= 100 ? (t("LAB_SCRAPS_STATUS_OVER") || "Πάνω από φουλ") :
                                value >= 75 ? (t("LAB_SCRAPS_STATUS_HIGH") || "Υψηλό") :
                                    value >= 40 ? (t("LAB_SCRAPS_STATUS_OK") || "ΟΚ") :
                                        (t("LAB_SCRAPS_STATUS_LOW") || "Χαμηλό");

                        return (
                            <div key={key} className={`scrap-row ${isOver ? "over" : ""}`}>
                                <div className="scrap-label">
                                    <span className="scrap-name">{label}</span>
                                    <span className="scrap-percent" aria-hidden="true">{value}%</span>
                                </div>

                                <div
                                    className="scrap-meter"
                                    role="meter"
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={Math.max(0, Math.min(value, 100))}
                                    aria-valuetext={`${label}: ${value}% (${status})`}
                                    data-status={isOver ? "over" : value >= 75 ? "high" : value >= 40 ? "ok" : "low"}
                                >
                                    <div className="scrap-fill" style={{width: `${capped}%`}}>
                                        <span className="scrap-fill-text">{value}%</span>
                                    </div>

                                    {isOver && (
                                        <span className="scrap-over-badge"
                                              title={t("LAB_SCRAPS_OVER_BADGE") || "Overflow"}>
                                          +{value - 100}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </section>
                {/* CTA */}
                <section className="lab-cta-container">
                    <a href="/memberships" className="cta-btn primary">
                        {t("LAB_JOIN_BTN") || "Γίνε μέλος"}
                    </a>
                    <a href="/information" className="cta-btn">
                        {t("LAB_PROPOSE_BTN") || "Κάνε μια πρόταση"}
                    </a>
                </section>
            </div>
        </>
    );
}
